import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { useUrlString, useUrlBool } from '../../hooks/useUrlState';
import { Card, Select, DatePicker, Space, Button, Input, Row, Col, StatCard, Popover, Modal, Tag, Tooltip, message, theme, cn } from '../../ui';
import { Landmark, Wallet, Building2, X, Star, ArrowDownLeft, ArrowUpRight, Scale, Download, Undo2, ArrowLeftRight, AlertTriangle, Receipt, CreditCard, CalendarDays } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { useFocusRow } from '../../hooks/useFocusRow';
import { RetailerLink, OrderLink, UserLink } from '../../components/EntityLinks';
import { useUrlParam } from '../../hooks/useUrlTab';
import { createPortal } from 'react-dom';
import { ExportMenu } from '../../components/ExportMenu';
import { BankExportModal } from './BankExportModal';
import { formatIstDate, formatIst } from '../../lib/time';
import { RoleBadge } from '../../components/RoleBadge';
import { bankAcctOption } from './PaymentFormModal';
import { ImportDialog, BankReconciliationWorkspace } from './BankReconciliationPage';
import { useFilterPanel, FilterRange, hueOption, type FilterChip } from '../../components/FilterPanel';
import { CashMovementModal } from './CashMovementModal';
import { inr2 } from '../../lib/format';
import { PALETTE, TAG_TONE_HUE } from '../../lib/palette';

const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];
// Money that hides trailing ".00": 2 decimals only when there are non-zero paise (₹4,72,024 but ₹1,274.50).
const inrT = inr2;

/** A seller-bank-account row's label: bank name ·· last-4 (+ nickname), or the cash/unassigned bucket. */
function bankTitle(b: any) {
  if (b.bankAccountId === 'ALL') {
    return { icon: <Landmark size={16} />, name: 'All accounts', sub: 'every account and the cash bucket together' };
  }
  if (!b.bankAccountId) return { icon: <Wallet size={16} />, name: 'Cash / Unassigned', sub: 'cash in hand — collections kept as cash, withdrawals, legacy' };
  /* An id on a ledger row that matches no bank account. It used to render as a nameless "Bank
     account" — indistinguishable from a real one, and inviting a reconciliation of something that
     does not exist. Named for what it is, with the id, so somebody can go and find the rows. */
  if (b.unknownAccount) {
    return { icon: <AlertTriangle size={16} />, name: `Unrecognised account #${b.bankAccountId}`, sub: 'these entries name an account that no longer exists' };
  }
  const name = b.bankName || b.label || 'Bank account';
  const acct = b.last4 ? ` ··${b.last4}` : '';
  const sub = b.label && b.label !== b.bankName ? b.label : null;
  return { icon: <Landmark size={16} />, name: `${name}${acct}`, sub };
}
const keyOf = (b: any) => (b.bankAccountId ?? 'NONE');

/**
 * An account's entry counts: collections in, staff payouts out — a direction arrow and its figure, in
 * the same green-in / red-out colour language as the Money in / Money out columns beside it.
 *
 * The spacing is the whole trick here. Each arrow sits tight against its own figure (gap-1) and the two
 * pairs are held well apart (gap-3), so the eye groups them as two units instead of four loose glyphs.
 * Each pair is a fixed-width box packed from the LEFT, which is what makes the two arrow columns run
 * dead straight down the table and the figures start on a common edge. The two alternatives both broke
 * that: right-aligning the whole pair slid the arrow left as the figure grew (1, 2 and 3-digit counts
 * put the arrow in three different places), and right-aligning the figure alone opened a ragged gap
 * between arrow and number. Both pairs always render — a zero greys out rather than collapsing the row,
 * which is what left the original stacked "6 in" / "46 out" text ragged in the first place.
 */
function EntryCount({ n, dir }: { n: number; dir: 'in' | 'out' }) {
  const count = Number(n) || 0;
  const Icon = dir === 'in' ? ArrowDownLeft : ArrowUpRight;
  const noun = dir === 'in' ? 'collection' : 'payout';
  return (
    <Tooltip label={`${count} ${noun}${count === 1 ? '' : 's'}`}>
      <span
        className={cn(
          'inline-flex w-13 items-center gap-1 tabular-nums type-body-medium',
          count === 0
            ? 'text-subtle'
            : dir === 'in'
              ? 'text-success-600 dark:text-success-500'
              : 'text-danger-600 dark:text-danger-500',
        )}
      >
        <Icon className="size-3.5 shrink-0" />
        {count}
      </span>
    </Tooltip>
  );
}

/** Wallet ledger types that move real bank money, in the words finance uses for them. */
/** How a reconciliation's lifecycle reads to an accounts user. */
const RECON_LABEL: Record<string, string> = { DRAFT: 'Draft', REVIEWED: 'Reviewed', COMPLETED: 'Completed' };

const PAYOUT_LABEL: Record<string, string> = {
  ADVANCE_IN: 'Advance to staff',
  REIMBURSEMENT: 'Expense reimbursement',
  // A reimbursement already paid on a claim that was then reversed, handed back by the rep.
  RECOVERY: 'Reimbursement returned',
  RETURN: 'Advance returned',
  // Our own money leaving an account for cash (and, in the cash book, cash leaving for the bank).
  CASH_WITHDRAWAL: 'Cash withdrawn',
  CASH_DEPOSIT: 'Cash banked',
};

/** Register row type for a bank ↔ cash contra entry, named from the side you are looking at. */
const movementLabel = (r: any, cashSide: boolean): string => (r.movementKind === 'WITHDRAWAL'
  ? (cashSide ? 'Cash received from bank' : 'Cash withdrawn')
  : (cashSide ? 'Cash banked' : 'Cash deposited'));

/**
 * WHAT AN ENTRY IS — in ONE definition.
 *
 * The tone the Type column's tag wears and the hue of the dot its filter option carries both read
 * from here, so a collection cannot be green in the rows and blue in the picker that finds them.
 */
const ENTRY_TONE = { COLLECTION: 'green', PAYOUT: 'gold', MOVEMENT: 'blue' } as const;

/** The value the Type filter narrows by: the SUB-type — which payout, which leg of a contra — not
 *  the bare kind, because "Payout" alone would lump an advance in with a reimbursement. */
const entryTypeOf = (r: any): string => (r.kind === 'CASH_MOVEMENT' ? `CM_${r.movementKind}`
  : r.kind === 'PAYOUT' ? r.payoutType : 'COLLECTION');

const entryHue = (value: string): string => TAG_TONE_HUE[
  value.startsWith('CM_') ? ENTRY_TONE.MOVEMENT
    : value === 'COLLECTION' ? ENTRY_TONE.COLLECTION
      : ENTRY_TONE.PAYOUT
];

/**
 * WHICH RAIL THE MONEY TOOK — the one fact a mode carries that this register is built around.
 *
 * The page's top half is exactly this split: bank accounts under a Landmark, the cash bucket under a
 * Wallet. So CASH takes the gold the "Cash in hand" tile already wears, and every rail that lands in
 * an account — UPI, cheque, NEFT, RTGS, IMPS, card — takes the sky blue of the accounts beside it.
 * OTHER names no rail, so it keeps a hollow ring rather than being given a colour it cannot honour.
 *
 * Eight arbitrary hues, one per mode, was the alternative: it would have made the dot decorative and
 * said nothing a reader could use — an identity mark must vary for a REASON.
 */
const modeHue = (m: any): string | undefined => {
  const v = String(m ?? '').toUpperCase();
  if (v === 'CASH') return TAG_TONE_HUE.gold;
  return !v || v === 'OTHER' ? undefined : TAG_TONE_HUE.blue;
};

/**
 * Bank-wise collections: how much recorded (APPROVED, non-refund, non-credit) money landed in each
 * seller bank account. The summary lists every account with totals; picking a bank (dropdown or by
 * clicking a summary row) shows that account's full statement below with a running total — a dropdown
 * rather than inline expansion because a single account's statement can be long. Rendered as the
 * "By bank" tab of the Payments page; finance-only (the tab itself is gated on PAYMENT.APPROVE).
 */
/** `headerSlot` is the page-header element the summary-export button portals into (see the render). */
export function BankCollectionsView({ headerSlot }: { headerSlot?: HTMLElement | null } = {}) {
  const { token } = theme.useToken();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  // Downloading a full statement is its own permission (Admin/Accounts/SA by default, editable on
  // Roles & Permissions) — hide the export affordances entirely rather than let them 403.
  const canExport = usePermission('PAYMENT.STATEMENT.EXPORT');
  const canRecordMovement = usePermission('CASH.MOVEMENT.RECORD');
  const canReconcile = usePermission('BANK_RECONCILIATION.VIEW');
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Bank account id, or 'NONE' for the cash bucket. In the URL (`?bank=`) so the open statement — and
  // the row you clicked a name from — is still there when you come back from a retailer/staff page.
  const [selectedBank, setSelectedBank] = useUrlParam('bank');
  // Reconcile on an account with no reconciliation yet opens the IMPORT modal right here — landing the
  // user on an empty page just to press one button is a wasted step. Once a reconciliation exists, the
  // action goes to its workspace, which needs the full page width.
  const [importBank, setImportBank] = useState<any | null>(null);
  // Reconciliation runs in a modal over this page — the reviewer keeps their place in the bank list,
  // and closing puts them straight back on it.
  const [reconBank, setReconBank] = useState<any | null>(null);
  // The page-level export opens a dialog (period / accounts / format) rather than firing immediately —
  // the one-click version always produced the same file and gave no way to scope it.
  const [exportOpen, setExportOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  /*
   * WHAT THE REGISTER IS ACTUALLY SHOWING.
   *
   * The header block above the register printed the ACCOUNT's totals while the table under it could
   * be filtered — so a tile that opens a slice landed the reader on its own figure contradicted in
   * larger type: "Cash in hand ₹-26,443" clicked through to a card headed "Net ₹-19,412". The
   * register reports its on-screen figures up here, and the header states those; with no filter
   * applied they ARE the account's totals, so nothing changes for the ordinary case.
   */
  const [shownTotals, setShownTotals] = useState<any | null>(null);

  /* The summary and the KPIs show the CURRENT position — every account, all of it. Narrowing happens
     inside a register, where the rows being narrowed are on screen. */
  const params = useMemo(() => ({}) as Record<string, any>, []);

  const { data, isLoading } = useQuery({
    queryKey: ['payments-bank-summary', params],
    queryFn: () => request<any>({ url: '/payments/bank-summary', params }),
    placeholderData: keepPreviousData,
  });
  const banks: any[] = data?.banks ?? [];
  const grandTotal = data?.grandTotal ?? 0;
  const grandCount = data?.grandCount ?? 0;
  const grandPaidOut = data?.grandPaidOut ?? 0;
  const grandPayoutCount = data?.grandPayoutCount ?? 0;
  // How much of the two figures above is our own money changing place rather than trade.
  const grandMovedIn = data?.grandMovedIn ?? 0;
  const grandMovedOut = data?.grandMovedOut ?? 0;
  const grandNet = data?.grandNet ?? 0;
  // The bank desk's worklist: non-cash collections still carrying no bank account.
  const unassigned = data?.unassignedNonCash ?? { count: 0, total: 0 };

  /*
   * Cash in hand is a BALANCE, not a period figure, so it comes from its own endpoint rather than from
   * the date-filtered summary — the Cash / Unassigned row's net answers "what moved in this period",
   * which is a different question and would read as a float the company does not hold.
   */
  const canSeeCash = usePermission('CASH.MOVEMENT.VIEW');
  const { data: cashRaw, isError: cashFailed } = useQuery({
    queryKey: ['cash-in-hand'],
    queryFn: () => request<any>({ url: '/cash-movements/cash-in-hand' }),
    enabled: canSeeCash,
  });
  const cashInHand: number | null = canSeeCash && !cashFailed
    ? Number((cashRaw as any)?.cashInHand ?? (cashRaw as any)?.data?.cashInHand ?? 0)
    : null;

  /* Cash a field rep collected is no longer counted as office cash (it is not in the office), so the
     office figure alone no longer answers "how much cash does the company hold". The rep-held total
     is fetched to complete that sentence and to point the reader at the register below. */
  /* The DERIVED figure. This used to read the retired custody ledger, which kept reporting a balance
     the payments and the bank no longer agreed with — it showed ₹40,319 held when the real
     un-banked total was different, because the old ledger moved on hand-over events that no longer
     happen. Now it is approved cash collections minus the deposits allocated against them. */
  const canSeeCustody = usePermission('CASH.CUSTODY.VIEW');
  const { data: custody } = useQuery({
    queryKey: ['retailer-money-holders'],
    queryFn: () => request<any>({ url: '/retailer-money/holders' }),
    enabled: canSeeCustody,
  });
  const withReps: number = Number(custody?.totals?.outstanding ?? 0);


  // Latest reconciliation per bank account — drives the status chip and the Reconcile action.
  const { data: reconStatus } = useQuery({
    queryKey: ['recon-status-by-bank'],
    queryFn: () => request<any[]>({ url: '/bank-reconciliations/status-by-bank' }),
    enabled: canReconcile,
  });
  const reconByBank = new Map<string, any>(
    (Array.isArray(reconStatus) ? reconStatus : ((reconStatus as any)?.data ?? [])).map((r: any) => [String(r.bankAccountId), r]),
  );

  /* Which way the register should open. A tile that counts money OUT must land the reader on the
     out rows; landing them on an unfiltered register makes them re-derive by hand the very figure
     they clicked. Cleared as soon as a different bucket is chosen. */
  const [stmtDir, setStmtDir] = useState<'IN' | 'OUT' | undefined>();
  /* "To assign" counts one SLICE of the cash bucket — the non-cash collections still carrying no
     account — so its click has to carry that slice with it. Opening the bucket on direction alone
     landed the reader on all 44 money-in rows under a tile that had promised 6. */
  const [stmtNeedsBank, setStmtNeedsBank] = useState(false);
  /* "Cash in hand" is a BALANCE over one slice of the cash bucket — office cash only. Opening the
     whole bucket landed the reader on a register whose net is ₹-19,412 under a tile reading
     ₹-26,443: the figure they clicked was nowhere on the screen it opened. */
  const [stmtCashBook, setStmtCashBook] = useState(false);
  const openRegister = (bucket: string, dir?: 'IN' | 'OUT', opts: { needsBank?: boolean; cashBook?: boolean } = {}) => {
    setStmtDir(dir);
    setStmtNeedsBank(!!opts.needsBank);
    setStmtCashBook(!!opts.cashBook);
    setSelectedBank(bucket);
    /* A tile's click REPLACES the filter set. Leaving the reconciliation filter on while opening a
       different bucket showed a table narrowed to "accounts behind" that did not even contain the
       bucket whose register was open underneath — two filters from two tiles, neither announced. */
    setBehindOnly(false);
  };
  /* The reconciliation tile is a VIEW, like every other tile: it narrows the table to the accounts
     that are behind. Starting the work is the row's own Reconcile button, where the account being
     reconciled is on screen and named. */
  const [behindOnly, setBehindOnly] = useUrlBool('behind');

  /* The narrowing must use the SAME test as the tile's own fraction, or the tile counts one thing and
     opens another: "2 still to tie out" listed three rows, because an id that resolves to no account
     has no reconciliation status and so looked like an account that was behind. It is not an account
     (see `unknownAccount`), so it is neither in the denominator nor in what the tile reveals. */
  const isBehind = (b: any) => !!b.bankAccountId && !b.unknownAccount
    && reconByBank.get(String(b.bankAccountId))?.status !== 'COMPLETED';
  const rowsWithKey = banks
    .filter((b: any) => !behindOnly || isBehind(b))
    .map((b) => ({ ...b, _key: keyOf(b) }));
  /* `ALL` is not one of the summary's rows — it is the whole page. It is synthesised here so the
     register below can open on it exactly like a real account, which is what lets a whole-company
     tile ("Money in", "Money out") show the entries behind its own figure ON THIS PAGE instead of
     sending the reader to a different screen where the number does not appear. */
  const selected = selectedBank === 'ALL'
    ? {
        bankAccountId: 'ALL',
        total: grandTotal,
        paidOut: grandPaidOut,
        net: grandNet,
        payoutCount: grandPayoutCount,
        /* Counted once per movement by the server. Summing the accounts' own figures would double a
           cash-book deposit, which is carried on the bank row AND the cash row — each of those rows
           has its own gap to name. Without it the all-accounts header was the one register that never
           mentioned the money sitting between the books and the bank. */
        awaitingVerification: data?.grandAwaiting ?? 0,
        awaitingCount: data?.grandAwaitingCount ?? 0,
      }
    : selectedBank ? banks.find((b) => keyOf(b) === selectedBank) : null;
  /* The figures the register's header prints: what the register says it is showing, falling back to
     the summary row until it has reported (so the block is never momentarily blank). The bucket is
     carried on the report, so a figure from the account you just left cannot be shown against the
     one you just opened. */
  const headFig = (shownTotals && String(shownTotals.bucket) === String(selectedBank))
    ? shownTotals
    : (selected ?? { total: 0, paidOut: 0, net: 0, awaitingVerification: 0, awaitingCount: 0 });

  // KPI figures (from the same summary payload the table uses).
  /* An id that resolves to no bank account is not an account (see `unknownAccount` on the server).
     It has no statement, so it can be neither reconciled nor counted among the accounts that are. */
  const realBanks = banks.filter((b) => b.bankAccountId && !b.unknownAccount);
  // Reconciliation progress across the real accounts, and how far behind the worst one is.
  const reconDone = realBanks.filter((b) => reconByBank.get(String(b.bankAccountId))?.status === 'COMPLETED').length;
  const reconGapDays = (() => {
    const days = realBanks.map((b) => {
      const st = reconByBank.get(String(b.bankAccountId));
      if (!st?.statementToDate) return null; // never reconciled — reported separately below
      return Math.max(0, Math.round((Date.now() - new Date(`${String(st.statementToDate).slice(0, 10)}T00:00:00`).getTime()) / 86_400_000));
    });
    const known = days.filter((d): d is number => d != null);
    return known.length ? Math.max(...known) : null;
  })();
  const reconNeverStarted = realBanks.filter((b) => !reconByBank.get(String(b.bankAccountId))).length;
  // What the "Accounts reconciled" tile opens: never-started accounts first (they are furthest behind),
  // then any still in draft/review. Undefined once everything is completed.
  const nextToReconcile = realBanks.find((b) => !reconByBank.get(String(b.bankAccountId)))
    ?? realBanks.find((b) => reconByBank.get(String(b.bankAccountId))?.status !== 'COMPLETED');
  const cashBucket = banks.find((b) => !b.bankAccountId);
  const inBanksTotal = Math.round((grandTotal - (cashBucket?.total ?? 0)) * 100) / 100;

  /**
   * One account's statement, in whichever format the user picks — the rows behind that summary row,
   * under the SAME filters (date / mode / retailer) currently applied, so the file's total ties to the
   * total on screen. Offered per row (export any account without opening it) and in the open
   * statement's header.
   */
  /* `extra` is what the open register is currently filtered by. The export endpoint understands
     period, mode and retailer; the register's other controls (type, document, party text, reference,
     direction) are applied to the fetched rows and have no server equivalent, which the register warns
     about rather than shipping a file that quietly disagrees with the screen. */
  const bankExportProps = (b: any, extra: Record<string, string> = {}) => {
    const key = String(keyOf(b));
    const slug = bankTitle(b).name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const q: Record<string, string> = { ...params, ...extra, bankAccountId: key };
    const period = q.from && q.to ? `-${q.from}-to-${q.to}` : q.from ? `-from-${q.from}` : q.to ? `-upto-${q.to}` : '';
    return {
      baseUrl: '/payments/bank-statement',
      query: new URLSearchParams(q).toString(),
      filename: `bank-statement-${slug}${period}`,
    };
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: 'bank', title: 'Bank account', alwaysVisible: true, width: 320,
      sortable: true, sorter: (a: any, b: any) => bankTitle(a).name.localeCompare(bankTitle(b).name),
      render: (_: any, b: any) => {
        const t = bankTitle(b);
        return (
          <div className="flex items-center gap-2">
            <span style={{ color: b.bankAccountId ? token.colorPrimary : token.colorTextTertiary }}>{t.icon}</span>
            <div>
              <div className="flex items-center gap-2" style={{ fontWeight: 600 }}>
                {t.name}
                {b.isPrimary && <Tag tone="green" icon={<Star size={12} />} style={{ margin: 0 }}>Primary</Tag>}
              </div>
              {t.sub && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{t.sub}</div>}
            </div>
          </div>
        );
      },
    },
    {
      // Both directions get a count. This column used to be "Payments" and counted collections only,
      // which under-reported an account the moment payouts appeared beside them.
      key: 'count', title: 'Entries', width: 135, sortable: true,
      sorter: (a: any, b: any) => (a.count + a.payoutCount) - (b.count + b.payoutCount),
      render: (_: any, b: any) => (
        <div className="flex items-center gap-3">
          <EntryCount n={b.count} dir="in" />
          <EntryCount n={b.payoutCount} dir="out" />
        </div>
      ),
    },
    {
      // The two biggest channels, then a count. An account can collect through all seven modes, and
      // listing them all wrapped into three ragged lines — which buried the two that carry the money,
      // and made that one row three times the height of its neighbours, so the whole table looked
      // uneven. The full breakdown is a hover away, the same way Money out already handles its types.
      key: 'byMode', title: 'By mode', width: 260,
      render: (_: any, b: any) => {
        const entries = (Object.entries(b.byMode ?? {}) as [string, any][]).sort((a, x) => x[1] - a[1]);
        if (!entries.length) return <span style={{ color: token.colorTextTertiary }}>—</span>;
        const shown = entries.slice(0, 2);
        const rest = entries.length - shown.length;
        return (
          <Tooltip label={entries.map(([m, amt]) => `${m} ${inrT(amt)}`).join(' · ')}>
            <span className="flex items-baseline gap-x-3 truncate whitespace-nowrap text-xs">
              {shown.map(([m, amt]) => (
                <span key={m}>
                  <span style={{ color: token.colorTextTertiary }}>{m}</span> {inrT(amt)}
                </span>
              ))}
              {rest > 0 && <span className="shrink-0 text-subtle">+{rest} more</span>}
            </span>
          </Tooltip>
        );
      },
    },
    { key: 'lastDate', title: 'Last received', width: 130, dataIndex: 'lastDate', sortable: true, render: (_: any, b: any) => b.lastDate ? <span className="whitespace-nowrap">{formatIstDate(b.lastDate)}</span> : <span style={{ color: token.colorTextTertiary }}>—</span> },
    // Money in / money out are read at a glance, so they carry the ledger's own colour language:
    // green = came in, red = went out. Same pair as the register table below and the KPI tiles above.
    { key: 'total', title: 'Money in', width: 150, dataIndex: 'total', sortable: true, render: (_: any, b: any) => <b className="whitespace-nowrap text-success-600 dark:text-success-500">{inrT(b.total)}</b> },
    {
      // Staff advances + expense reimbursements funded from this account. Sourced from the wallet
      // ledger — a payout can never be a `payments` row, since that table requires a retailer.
      key: 'paidOut', title: 'Money out', width: 160, dataIndex: 'paidOut', sortable: true,
      render: (_: any, b: any) => {
        if (!b.payoutCount) return <span style={{ color: token.colorTextTertiary }}>—</span>;
        const types = Object.entries(b.byPayoutType ?? {}).sort((a: any, x: any) => x[1] - a[1]);
        return (
          <Tooltip label={types.map(([t, amt]: any) => `${PAYOUT_LABEL[t] ?? t}: ${inrT(amt)}`).join(' · ')}>
            <span className="whitespace-nowrap font-semibold text-danger-600 dark:text-danger-500">
              −{inrT(b.paidOut)}
            </span>
          </Tooltip>
        );
      },
    },
    { key: 'net', title: 'Net', width: 150, dataIndex: 'net', sortable: true, render: (_: any, b: any) => <b className="whitespace-nowrap">{inrT(b.net)}</b> },
    ...(canReconcile ? [{
      // Reconciliation status + entry point. The cash/unassigned bucket has no bank statement to
      // reconcile against, so it is deliberately not offered there.
      // 230, not 190: the cell holds a 6rem status slot + the Reconcile button, which together need
      // ~224px. At 190 the button overflowed into the export column and sat under its download icon
      // on every screen narrow enough for the table to stop stretching (≈1900px and below).
      key: 'recon', title: 'Reconciliation', width: 230, alwaysVisible: true,
      render: (_: any, b: any) => {
        if (!b.bankAccountId) return <span style={{ color: token.colorTextTertiary }}>—</span>;
        /* Nothing to reconcile against: there is no such account, so there is no statement. */
        if (b.unknownAccount) {
          return <span className="type-caption text-warning-600 dark:text-harvest-300">no such account</span>;
        }
        const st = reconByBank.get(String(b.bankAccountId));
        const label = st ? RECON_LABEL[st.status] ?? st.status : 'Not started';
        const tone = st?.status === 'COMPLETED' ? 'green' : st?.status === 'REVIEWED' ? 'blue' : st ? 'gold' : 'default';
        return (
          <div className="flex items-center gap-2">
            {/* Fixed-width slot: the Tag sizes to its own text, so "Draft" / "Completed" / "Not started"
                each pushed the Reconcile button to a different x and the action column zig-zagged down
                the table. Reserving the widest label's worth of space lines the buttons up. */}
            <span className="w-24 shrink-0">
              <Tag tone={tone as any} style={{ margin: 0 }}>{label}</Tag>
            </span>
            <Button variant="text" size="sm" icon={<Scale size={16} />}
              onClick={(e: any) => { e.stopPropagation(); if (st) setReconBank(b); else setImportBank(b); }}>
              Reconcile
            </Button>
          </div>
        );
      },
    }] : []),
    ...(canExport ? [{
      // Per-account export, right where the account's total is — no need to open the statement first.
      key: 'export', title: '', width: 60, align: 'right' as const, alwaysVisible: true,
      // stopPropagation: the row's own onClick selects the bank, and opening a menu must not also
      // toggle the statement open underneath it.
      render: (_: any, b: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <ExportMenu
            {...bankExportProps(b)}
            iconOnly
            variant="text"
            title={`Export ${bankTitle(b).name} statement`}
          />
        </span>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-3">
      {/* The page-level export belongs beside the page title, next to Record Payment — but the filters
          it must honour live down here. Rather than lift all of that state up, the button is rendered
          here and PORTALLED into the slot the page reserves for it. */}
      {headerSlot && createPortal(
        <>
          {/* Our own money moving between an account and cash. It belongs beside the account list it
              restates, not on a page of its own. */}
          {canRecordMovement && (
            <Button size="sm" icon={<ArrowLeftRight size={16} />} onClick={() => setMovementOpen(true)}>Cash movement</Button>
          )}
          {canExport && <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export summary</Button>}
        </>,
        headerSlot,
      )}
      <CashMovementModal open={movementOpen} onClose={() => setMovementOpen(false)} />
      <BankExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        banks={banks.map((b) => ({ key: String(keyOf(b)), name: bankTitle(b).name, sub: bankTitle(b).sub }))}
        baseParams={params}
        // The page no longer holds a period — the export dialog asks for its own, which is where the
        // question belongs anyway (you export a statement FOR a period).
        initialRange={null}
        loading={isLoading}
      />
      {/* KPI cards — both directions, so the figures tie to a real bank statement rather than to
          collections alone. "Money out" is staff advances and expense reimbursements funded from a
          company account; those live in the wallet ledger, not in `payments`. */}
      <Row align="stretch" gutter={[12, 12]}>
        {/* The hints name what the figure is MADE of. Once cash movements exist, "46 collections" would
            be describing a total that also contains banked cash — so the deposit/withdrawal share is
            called out rather than folded in silently. */}
        {/* Every tile leads to the rows behind its number. Money in and Money out drill OUT of this
            page, because the individual entries live elsewhere: collections in the payments list,
            payouts in the staff wallets. The rest move this page's own state. */}
        {/* Money in and Money out open the register UNDERNEATH, across every account, filtered to
            that direction — the rows the figure is made of, on the page the figure is on. They used
            to navigate away (to the payments list, to the wallets page), where the entries are only
            half the story anyway: collections live in `payments`, payouts in `wallet_ledger`, and
            neither of those pages shows both. */}
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard label="Money in" value={inrT(grandTotal)} icon={<ArrowDownLeft />} tone="success"
          active={selectedBank === 'ALL' && stmtDir === 'IN'}
          onClick={() => openRegister('ALL', 'IN')}
          /* A contra has TWO legs and this is a whole-company view, so the same movement lands in both
             tiles — into the bank and out of cash, or the reverse. The hint therefore names the
             MOVEMENT, not one direction of it: "withdrawn as cash" was hard-coded under Money out and
             read as a withdrawal even when every movement on the page was a deposit. */
          /* "entries", not "collections": money in is retailer collections PLUS cash banked or drawn
             PLUS an unspent staff advance handed back. Calling the count collections named only the
             largest part of it, and the register it opens has always shown all three. */
          hint={grandMovedIn > 0
            ? `${grandCount} entries · incl. ${inrT(grandMovedIn)} from cash ↔ bank movements`
            : `${grandCount} entr${grandCount === 1 ? 'y' : 'ies'} recorded here`} /></Col>
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard label="Money out" value={inrT(grandPaidOut)} icon={<ArrowUpRight />} tone="danger"
          active={selectedBank === 'ALL' && stmtDir === 'OUT'}
          onClick={() => openRegister('ALL', 'OUT')}
          hint={grandMovedOut > 0
            ? `${grandPayoutCount} entries · incl. ${inrT(grandMovedOut)} from cash ↔ bank movements`
            : `${grandPayoutCount} staff payout${grandPayoutCount === 1 ? '' : 's'} recorded here`} /></Col>
        {/* Net is the whole-company figure, so its click is "stop looking at one account". Active
            (and inert) when nothing is selected — that is already the state it would put you in. */}
        {/*
          * These three figures are what THIS SYSTEM recorded moving through the accounts — not the
          * accounts' true turnover. The same account may be used for things the ERP never sees, and
          * those entries appear only on the bank's own statement. Closing that gap is what the
          * reconciliation tile at the end of this band is for, so the hint says "recorded here"
          * rather than implying the books and the bank agree.
          */}
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard label="Net movement" value={inrT(grandNet)} icon={<Landmark />} tone="brand"
          active={selectedBank === 'ALL' && !stmtDir}
          onClick={() => (selectedBank === 'ALL' && !stmtDir ? setSelectedBank(undefined) : openRegister('ALL'))}
          hint="in − out, as recorded here" /></Col>

        {/*
          * The three tiles below replace a plain "Bank accounts 4" count. A count of accounts decides
          * nothing — the table under it already lists them — while these are the questions this desk
          * actually opens the page with: how much cash are we holding, what is still unattributed, and
          * are the accounts tied out.
          */}

        {/* A BALANCE, not a period figure (see cashInHand above). "—" when the reader lacks
            CASH.MOVEMENT.VIEW or the figure could not be loaded — never a 0 that reads as "no cash". */}
        {/* Amber while it is a float we hold; RED when it goes negative, because the books are then
            claiming we hold less than nothing — which is never true of cash, only of a collection that
            has not been entered. Colour is the fastest way to say "this is a data problem". */}
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard
          label="Cash in hand" icon={<Wallet />}
          tone={cashInHand != null && cashInHand < 0 ? 'danger' : 'gold'}
          // Opens the cash book itself — the register of what makes this number up.
          active={selectedBank === 'NONE' && !stmtDir && stmtCashBook}
          onClick={cashInHand == null ? undefined : () => openRegister('NONE', undefined, { cashBook: true })}
          value={cashInHand == null ? '—' : inrT(cashInHand)}
          /*
           * THE PROBLEM FIRST, when there is one.
           *
           * The rep-held clause was tested first, so a NEGATIVE office float — a red figure claiming
           * the company holds less than nothing — was captioned "in the office · ₹28,613 more is with
           * field staff" and the one line that explains it could never be reached. The explanation now
           * leads, and the field-staff link rides along after it: both facts are true at once, and the
           * reader still needs the way through to the rep cash tab.
           */
          /* SHORT ENOUGH TO BE READ. StatCard clamps a hint to two lines, so the first wording — a
             full diagnosis plus the field-staff clause — was cut off mid-figure at "₹28,613…", which
             hides exactly the number the sentence exists to hand over. */
          hint={cashInHand == null ? 'not available to you' : (
            <>
              {cashInHand < 0 ? 'more paid out than recorded in' : 'in the office'}
              {withReps > 0.005 && (
                <> · <Link to="/billing/payments?tab=repcash" className="text-brand-700 hover:underline dark:text-brand-400"
                  onClick={(e: any) => e.stopPropagation()}>{inrT(withReps)} with field staff</Link></>
              )}
              {cashInHand >= 0 && withReps <= 0.005 && ', in no bank account'}
            </>
          )} /></Col>

        {/* The worklist. Clicking OPENS the bucket holding exactly these entries; with nothing to
            assign it is a statement, not a control, so the click (and the hover lift) come off. */}
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard
          label="To assign" tone={unassigned.count > 0 ? 'warning' : 'neutral'} icon={<Landmark />}
          value={inrT(unassigned.total)}
          active={unassigned.count > 0 && selectedBank === 'NONE' && stmtDir === 'IN' && stmtNeedsBank}
          /* Opens exactly the rows this figure counts. Without the third argument the click landed on
             every money-in row in the cash bucket — cash collections and cash drawn from the bank
             included — so a tile reading "6 non-cash collections with no bank" produced 44 entries. */
          onClick={unassigned.count > 0 ? () => openRegister('NONE', 'IN', { needsBank: true }) : undefined}
          hint={unassigned.count > 0
            ? `${unassigned.count} non-cash collection${unassigned.count === 1 ? '' : 's'} with no bank`
            : 'every non-cash collection is attributed'} /></Col>

        {/* Achieved figure first, denominator kept: "2 / 4" reads as two of four DONE. */}
        {/* Straight into the work: the first account that is not tied out. With every account done
            there is nothing to open, so the click comes off rather than leading somewhere pointless. */}
        <Col xs={24} sm={12} lg={8} xl={4}><StatCard
          label="Accounts reconciled" tone={reconDone === realBanks.length && realBanks.length > 0 ? 'success' : 'sky'}
          icon={<Scale />}
          /* Shows WHICH accounts are behind, rather than launching an import wizard for one of them
             that the reader never picked. A number is a question ("which one?"); answering it with a
             file-upload dialog answers a different question, hides the other three accounts, and
             starts a write from something that reads as a metric. The row's own Reconcile button
             starts the work, with the account named on screen. */
          active={behindOnly}
          onClick={realBanks.length > reconDone
            ? () => {
                // Same rule in the other direction: narrowing to the accounts behind closes any
                // register a previous tile had opened, so the page shows one thing at a time.
                const next = !behindOnly;
                setBehindOnly(next);
                if (next) { setSelectedBank(undefined); setStmtDir(undefined); }
              }
            : undefined}
          value={realBanks.length ? `${reconDone} / ${realBanks.length}` : '—'}
          /* The hint must describe what the CLICK reveals. It used to report only the never-started
             accounts ("1 never reconciled") while the click shows every account not tied out — so
             a reader following a hint that promised one row was handed three. The headline counts
             what is DONE; the hint counts what is left, and names the never-started ones as the
             subset they are. */
          hint={!realBanks.length ? 'no bank accounts yet'
            : realBanks.length - reconDone > 0
              ? `${realBanks.length - reconDone} still to tie out${reconNeverStarted ? ` · ${reconNeverStarted} never started` : ''}`
              : reconGapDays != null ? `all tied out, to ${reconGapDays}d ago`
              : 'every account tied out'} /></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="payments-bank-summary"
          rowKey="_key"
          layoutFixed
          loading={isLoading}
          dataSource={rowsWithKey}
          columns={columns}
          emptyText="No recorded collections for these filters"
          rowClassName={(b: any) => (String(keyOf(b)) === String(selectedBank) ? 'bg-brand-500/5' : '')}
          onRow={(b: any) => ({
            /*
             * Clicking a bank row picks it (toggles) → its statement shows below, same as the Bank
             * dropdown. The direction filter is cleared with it: "Money in" belongs to the TILE that
             * was clicked, not to the bank. Carrying it over meant picking an account silently opened
             * a half register — 15 of 70 rows, money out hidden — and the reader had no reason to
             * suspect the total in front of them was filtered.
             */
            onClick: () => {
              setStmtDir(undefined);
              // Same argument for the register's seeded filters: they belong to the TILE that set
              // them, not to whichever account the reader opens next.
              setStmtNeedsBank(false);
              setStmtCashBook(false);
              setSelectedBank(selectedBank === keyOf(b) ? undefined : keyOf(b));
            },
            style: { cursor: 'pointer' },
          })}
          pagination={false}
          toolbarLeft={
            /*
             * NO FILTER BAR HERE — deliberately.
             *
             * This table answers one question: what does each account hold right now. Every control
             * that used to sit above it asked about ENTRIES (which mode, whose payment, over what
             * period), and entries only become visible once a register is open — so a reader set a
             * filter here and watched a table of account totals change for reasons they could not see.
             * The account picker was worse: it filtered nothing at all, it chose which register opened.
             *
             * The row IS the selector, and every entry-level question now lives in the register's own
             * Filters panel, next to the rows it changes.
             */
            undefined
          }
        />
      </Card>

      {/* The reconciliation workspace itself — a modal, so Payments stays underneath. */}
      <Modal
        open={!!reconBank}
        onOpenChange={(o: boolean) => { if (!o) { setReconBank(null); qc.invalidateQueries({ queryKey: ['recon-status-by-bank'] }); qc.invalidateQueries({ queryKey: ['recon-current'] }); } }}
        title="Reconcile with bank statement"
        description="The statement on the left, what the ERP knows on the right"
        width={1680}
      >
        {/* The workspace header names the account itself now, inline with the reconciliation number and
            the statement dates. A standalone line above it said the same thing twice. */}
        {reconBank && <BankReconciliationWorkspace bankAccountId={String(reconBank.bankAccountId)} />}
      </Modal>

      {/* First reconciliation for an account: import the statement from right here, then open it. */}
      {importBank && (
        <ImportDialog
          open
          bankAccountId={String(importBank.bankAccountId)}
          onClose={() => setImportBank(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['recon-status-by-bank'] });
            // Straight from importing into reviewing, without leaving the page.
            const b = importBank; setImportBank(null); setReconBank(b);
          }}
        />
      )}

      {/* Selected-bank statement — full width, not cramped in an expand row. */}
      {selected && (
        <Card>
          {/*
            * FOUR FIGURES, EACH WITH A NAME ON IT — not one sentence held together by dots.
            *
            * They were rendered as "· in ₹11,32,870.80 · out ₹42,876 · net ₹10,89,994.80 · ₹1,650
            * claimed, awaiting confirmation": four different quantities at one size, in one line,
            * separated by a punctuation mark. Nothing said which was which without reading the whole
            * string, and the fourth — the reconciling item, the one an accountant needs most — was
            * indistinguishable from the three that are ordinary totals.
            *
            * Laid out as small labelled blocks they are scannable, the money keeps its own colour
            * convention (in green, out red, net neutral), and the awaiting figure is visibly set apart
            * because it is NOT part of the arithmetic beside it.
            */}
          <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span style={{ color: selected.bankAccountId ? token.colorPrimary : token.colorTextTertiary }}>{bankTitle(selected).icon}</span>
              <span className="type-body-strong text-content">{bankTitle(selected).name}</span>
            </div>

            <div className="flex flex-wrap items-start gap-x-5 gap-y-2">
              <div>
                <div className="type-label text-subtle">Money in</div>
                <div className="whitespace-nowrap type-body-strong tabular-nums text-success-600 dark:text-success-500">{inrT(headFig.total)}</div>
              </div>
              {(headFig.paidOut > 0.005 || selected.payoutCount > 0) && (
                <div>
                  <div className="type-label text-subtle">Money out</div>
                  <div className="whitespace-nowrap type-body-strong tabular-nums text-danger-600 dark:text-danger-500">{inrT(headFig.paidOut)}</div>
                </div>
              )}
              <div>
                <div className="type-label text-subtle">Net</div>
                <div className="whitespace-nowrap type-body-strong tabular-nums text-content">{inrT(headFig.net)}</div>
              </div>
              {/* THE RECONCILING ITEM, stated on the register it belongs to and set apart from the
                  totals. Those are confirmed money only, so they can tie to the account — but an
                  accountant comparing this to a real statement has to know what is sitting between
                  the two, or they go hunting for a difference the system already knows about. */}
              {Number(headFig.awaitingVerification ?? 0) > 0.005 && (
                /*
                 * ON ONE LINE, unlike the three beside it — and deliberately.
                 *
                 * Those are stats: a label naming a column of the arithmetic, with its figure under it.
                 * This is not part of that arithmetic at all, it is a note ABOUT it, so it reads as a
                 * sentence in a pill rather than a fourth column. Stacked it also looked like the
                 * number had wrapped for want of room, on a row with most of its width unused.
                 */
                /* AND HOW MANY ROWS THAT IS. The KPI tile above counts entries that moved money, and
                   this register also shows the claims that have not — so the two differ by exactly
                   this many lines. Naming the count turns "the tile said 92 and I can see 94" into
                   arithmetic the reader can finish. */
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-control border border-warning-500/30 bg-warning-500/10 px-2.5 py-1.5 text-warning-600 dark:text-harvest-300">
                  <AlertTriangle size={14} className="shrink-0" />
                  <b className="tabular-nums">{inrT(headFig.awaitingVerification)}</b>
                  <span className="type-caption">
                    {Number(headFig.awaitingCount ?? 0) > 0
                      ? `claimed on ${headFig.awaitingCount} entr${headFig.awaitingCount === 1 ? 'y' : 'ies'} — outside the totals until confirmed`
                      : 'claimed, not yet confirmed'}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* WHAT THE TOOLBAR ABOVE IS ALREADY DOING TO THESE ROWS.
              The register is fetched with the page's params, so a mode/retailer/period set at the top
              silently removes rows here. Naming them is the difference between "there are no other
              entries" and "you are looking at a filtered slice" — and each one can be cleared from
              where it is shown. */}

          {/* Export / Close ride in the filter row rather than on a line of their own — one toolbar,
              filters left, actions right. */}
          <BankStatement
            bankAccountId={selected.bankAccountId}
            initialDir={stmtDir}
            initialNeedsBank={stmtNeedsBank}
            initialCashBook={stmtCashBook}
            onShown={setShownTotals}
            params={params}
            actions={(exportParams) => (
              <>
                {canExport && <ExportMenu {...bankExportProps(selected, exportParams)} label="Export" />}
                <Button variant="text" size="sm" icon={<X size={16} />} onClick={() => setSelectedBank(undefined)}>Close</Button>
              </>
            )}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * Per-bank REGISTER: everything that moved through this account, both directions, oldest-first with a
 * running NET balance — retailer collections in, staff advances and expense reimbursements out.
 *
 * It reads `/payments/bank-register` rather than the payments list, because the two halves live in
 * different tables: collections in `payments`, payouts in `wallet_ledger`. That is precisely why money
 * paid out of a company account used to be invisible on this page, and why the account could not be
 * reconciled against its real bank statement from here.
 */
/**
 * An amount that is ON the row but NOT in the running net.
 *
 * A deposit nobody has confirmed yet is a claim, so it is deliberately left out of the balance — but
 * printed in the same bold red as every real movement it looked like an arithmetic bug: two −₹10,000
 * rows one above the other, both leaving the running net at ₹-2,312. Struck through and muted, the
 * row says what it is at a glance: this figure is waiting, and the balance is right to ignore it.
 */
function PendingAware({ r, text, tone }: { r: any; text: string; tone: string }) {
  if (!r.pendingVerification) return <span className={`whitespace-nowrap font-medium ${tone}`}>{text}</span>;
  return (
    <Tooltip label="Claimed by the depositor and not yet confirmed in the bank, so it is not counted in the running net.">
      <span className="whitespace-nowrap text-subtle line-through decoration-1">{text}</span>
    </Tooltip>
  );
}

function BankStatement({ bankAccountId, params, actions, initialDir, initialNeedsBank, initialCashBook, onShown }: {
  bankAccountId: string | null;
  params: Record<string, any>;
  /* A FUNCTION, not a node: the filters that decide what the reader is looking at live in here, so the
     Export the parent renders has to be told what they are. Passing a ready-made node meant Export
     shipped the whole account whatever the screen showed. */
  actions?: (exportParams: Record<string, string>) => React.ReactNode;
  initialDir?: 'IN' | 'OUT';
  /** Seeded by the "To assign" tile — see its onClick. */
  initialNeedsBank?: boolean;
  /** Seeded by the "Cash in hand" tile — the office cash book, which is a slice of this bucket. */
  initialCashBook?: boolean;
  /** Reports the on-screen figures up, so the card header can state what is actually listed. */
  onShown?: (t: {
    bucket: string; total: number; paidOut: number; net: number;
    awaitingVerification: number; awaitingCount: number; payoutCount: number;
  }) => void;
}) {
  const canEdit = usePermission('PAYMENT.EDIT');
  const canReverseMovement = usePermission('CASH.MOVEMENT.REVERSE');
  const bankId = bankAccountId ?? 'NONE';
  // Which side of a contra entry this register is: on the cash side a withdrawal is money IN.
  const isCashBucket = bankId === 'NONE';
  const [fParty, setFParty] = useUrlString('party');
  const [fMode, setFMode] = useUrlString<string | undefined>('mode', undefined);
  /* The PERIOD lives here too, and goes to the SERVER rather than being applied to fetched rows: the
     running net and the header's in/out have to be computed over the window, not sliced after it.
     TWO independent bounds, not a RangePicker: the range control only emits a COMPLETE pair, so
     "everything since 1 Aug" silently did nothing — and at this width it overflowed its cell into the
     Direction control beside it. The server applies each bound on its own. */
  const [fFrom, setFFrom] = useState<Dayjs | null>(null);
  const [fTo, setFTo] = useState<Dayjs | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bank-register', bankId, params, fFrom?.format('YYYY-MM-DD'), fTo?.format('YYYY-MM-DD')],
    queryFn: () => request<any>({
      url: '/payments/bank-register',
      params: {
        ...params,
        bankAccountId: bankId,
        ...(fFrom ? { from: fFrom.format('YYYY-MM-DD') } : {}),
        ...(fTo ? { to: fTo.format('YYYY-MM-DD') } : {}),
      },
    }),
  });
  // Bank-account options for the assign picker (React Query dedupes this shared key across rows).
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: canEdit,
  });
  const bankAccountsRaw = (Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []));

  // Register filters. Declared and rendered in the SAME ORDER as the columns they narrow —
  // Type · Document · Party · Mode · Reference · Money in/out — so the toolbar reads left-to-right
  // exactly like the table under it. (Date is inherited from the summary's range picker above.)
  const [fType, setFType] = useUrlString<string | undefined>('type', undefined);
  const [fDoc, setFDoc] = useUrlString('doc');
  /*
   * NO Mode and NO Party here — deliberately.
   *
   * This register is fetched with the PAGE's params ({...params, bankAccountId}), so the toolbar above
   * already narrows these rows by mode, retailer and period on the server. Repeating Mode and Party
   * inside the register gave two controls for one question that could silently contradict each other:
   * page "UPI" + register "CASH" returned an empty register and explained nothing. Page scope stays at
   * the top; this panel only asks what is specific to a row inside one account.
   */
  const [fRef, setFRef] = useUrlString('ref');
  /* Seeded from the tile that opened this register, and shown as a normal active filter — the
     reader can see why they are looking at half the rows, and clear it. */
  const [fDir, setFDir] = useUrlString<string | undefined>('dir', initialDir);
  /*
   * Follow the tile only when it actually CHANGES. This used to run on every mount, which is fine for
   * component state but would now overwrite the direction restored from the URL the moment the reader
   * pressed Back — putting them back on "all rows" after they had narrowed to one side.
   */
  const seededDir = useRef<string | undefined>(initialDir);
  useEffect(() => {
    if (seededDir.current !== initialDir) { seededDir.current = initialDir; setFDir(initialDir); }
  }, [initialDir, bankAccountId, setFDir]);
  /* The bank desk's worklist as an ordinary filter: collections received by a non-cash mode that
     still name no account. Seeded by the "To assign" tile the same way direction is, so the reader
     can SEE why the register holds six rows rather than the whole cash bucket — and clear it. */
  const [fNeedsBank, setFNeedsBank] = useUrlBool('needsbank', !!initialNeedsBank);
  const seededNeedsBank = useRef<boolean | undefined>(initialNeedsBank);
  useEffect(() => {
    if (seededNeedsBank.current !== initialNeedsBank) { seededNeedsBank.current = initialNeedsBank; setFNeedsBank(!!initialNeedsBank); }
  }, [initialNeedsBank, bankAccountId, setFNeedsBank]);
  /* The office cash book — the rows behind the "Cash in hand" balance. Same seeded-filter pattern,
     so the reader can see why the bucket is narrowed and take the narrowing off. */
  const [fCashBook, setFCashBook] = useUrlBool('cashbook', !!initialCashBook);
  const seededCashBook = useRef<boolean | undefined>(initialCashBook);
  useEffect(() => {
    if (seededCashBook.current !== initialCashBook) { seededCashBook.current = initialCashBook; setFCashBook(!!initialCashBook); }
  }, [initialCashBook, bankAccountId, setFCashBook]);
  const clearFilters = () => {
    setFType(undefined); setFDoc(''); setFRef(''); setFDir(undefined);
    setFParty(''); setFMode(undefined); setFFrom(null); setFTo(null);
    setFNeedsBank(false); setFCashBook(false);
  };
  const filterCount = [fType, fDoc || undefined, fRef || undefined, fDir, fParty || undefined, fMode, fFrom, fTo,
    fNeedsBank || undefined, fCashBook || undefined]
    .filter(Boolean).length;
  /* The filters applied in the browser — the export URL cannot express these. */
  const clientOnlyActive = [
    fType ? 'type' : null,
    fDoc.trim() ? 'document' : null,
    fParty.trim() ? 'party' : null,
    fRef.trim() ? 'reference' : null,
    fDir ? 'direction' : null,
  ].filter(Boolean) as string[];

  const all: any[] = data?.rows ?? [];
  // Type and Mode options come from the rows actually present, so the pickers never offer a value that
  // would return nothing for this account.
  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of all) {
      const v = entryTypeOf(r);
      if (v && !seen.has(v)) {
        seen.set(v, r.kind === 'CASH_MOVEMENT' ? movementLabel(r, isCashBucket)
          : v === 'COLLECTION' ? 'Collection' : (PAYOUT_LABEL[v] ?? v));
      }
    }
    // Each option wears the dot of the TAG it produces in the Type column — green collection, gold
    // payout, blue contra — so the picker and the rows it leaves behind are one colour, in the closed
    // trigger as much as in the open list.
    return [...seen].map(([value, label]) => hueOption(value, label, entryHue(value)));
  }, [all]);
  const modeOptions = useMemo(
    () => [...new Set(all.map((r) => r.mode).filter(Boolean))]
      .map((m: any) => hueOption(m, m, modeHue(m))),
    [all],
  );

  const rows = useMemo(() => {
    const has = (hay: any, needle: string) => String(hay ?? '').toLowerCase().includes(needle.trim().toLowerCase());
    const filtered = all.filter((r) => {
      const rowType = entryTypeOf(r);
      if (fType && rowType !== fType) return false;
      if (fMode && r.mode !== fMode) return false;
      if (fParty && !has(r.partyName, fParty)) return false;

      if (fDir === 'IN' && !(r.signed > 0)) return false;
      if (fDir === 'OUT' && !(r.signed < 0)) return false;
      if (fDoc && !has(r.docNo, fDoc)) return false;

      if (fRef && !has(r.reference, fRef)) return false;
      if (fNeedsBank && !r.needsBank) return false;
      if (fCashBook && !r.cashBook) return false;
      return true;
    });
    // Re-run the balance over the rows ON SCREEN, in date order, then attach it per row. Keeping the
    // server's whole-account figure would make a filtered view read as a balance it never had; running
    // it here in chronological order means column sorting can still reorder freely without the figure
    // losing its meaning — each row keeps the balance as of its own date within the filtered set.
    let run = 0;
    const chronological = filtered
      .slice()
      .sort((a, b) => String(a.txnDate).localeCompare(String(b.txnDate)) || (Number(a.id) - Number(b.id)))
      .map((r) => {
        /* A deposit a collector claims but nobody has checked yet is SHOWN and not COUNTED — the same
           rule the server applies to the account totals, so this column can never disagree with them.
           It stays on the list because the reconciler matches against exactly these rows: hide it and
           the real credit on the statement comes back as an unexplained "Bank only" line. */
        if (!r.pendingVerification) run = Math.round((run + r.signed) * 100) / 100;
        /* `legKey` comes from the server for a cash movement, which can appear TWICE on the
           all-accounts register — once as it looks on the bank account, once as it looks in the cash
           book. Keying on kind + id alone gave both legs the same key. */
        return { ...r, running: run, _key: r.legKey ?? `${r.kind}-${r.id}` };
      });
    /*
     * NEWEST FIRST on screen, oldest first for the arithmetic.
     *
     * The balance has to accumulate in date order or it means nothing — but showing the list that way
     * put whatever just happened at the BOTTOM of 159 rows. A cash movement recorded a minute ago was
     * invisible without scrolling the whole register, which reads exactly like it was never posted.
     * Each row keeps the balance as of its own date, so reversing the display costs nothing; the DATE
     * column sorter still flips it back for anyone reading it as a statement.
     */
    return chronological.reverse();
  }, [all, fType, fDoc, fRef, fDir, fParty, fMode, fNeedsBank, fCashBook]);
  /* A deposit waiting to be verified is a LINE on this register, and the approvals hub links straight
     to it (?focus=CASH_MOVEMENT-<id>). Matched on the row key rather than the id, because a payment
     and a cash movement can carry the same id — the key is what tells them apart, here and in the DOM. */
  const focus = useFocusRow(rows, { match: ['_key'], keyOf: (r: any) => String(r._key) });

  // Totals for what is actually shown, so the header never disagrees with the rows under it.
  const shown = useMemo(() => {
    const counted = rows.filter((r: any) => !r.pendingVerification);
    const inSum = counted.filter((r: any) => r.signed > 0).reduce((s: number, r: any) => s + r.signed, 0);
    const outSum = counted.filter((r: any) => r.signed < 0).reduce((s: number, r: any) => s - r.signed, 0);
    const r2 = (n: number) => Math.round(n * 100) / 100;
    /* One claim, counted once: a pending deposit rides on BOTH legs of the all-accounts register
       (into the bank, out of cash) and the server flags the second leg `awaitingCounted: false`, so
       the same ₹1,650 is not reported as ₹3,300 waiting. */
    const pending = rows.filter((r: any) => r.pendingVerification && r.awaitingCounted !== false);
    const awaiting = pending.reduce((s: number, r: any) => s + Math.abs(Number(r.amount ?? 0)), 0);
    return {
      received: r2(inSum), paidOut: r2(outSum), net: r2(inSum - outSum),
      awaiting: r2(awaiting), awaitingCount: pending.length,
      inCount: rows.filter((r: any) => r.signed > 0).length,
      outCount: rows.filter((r: any) => r.signed < 0).length,
    };
  }, [rows]);

  /* Tell the card header what is on screen, so it can never print the account's totals over a
     filtered table. Reported with the bucket it belongs to — the parent discards a report that
     arrives for a bucket it is no longer showing. */
  useEffect(() => {
    if (isLoading) return;
    onShown?.({
      bucket: bankId, total: shown.received, paidOut: shown.paidOut, net: shown.net,
      awaitingVerification: shown.awaiting, awaitingCount: shown.awaitingCount, payoutCount: shown.outCount,
    });
  }, [isLoading, bankId, shown, onShown]);

  const stmtCols: DataTableColumn<any>[] = [
    {
      /* TWO different facts, and a register needs both. The big date is the VALUE date — the day the
         money moved, the one a bank statement carries and reconciliation matches on. Underneath is
         when the entry was actually recorded, which is what answers "who typed this, and when". They
         are often the same day and sometimes days apart. */
      key: 'txnDate', title: 'Date', dataIndex: 'txnDate', sortable: true, width: 132,
      render: (v: any, r: any) => (
        <span className="block leading-tight">
          <span className="block whitespace-nowrap text-content">{formatIstDate(v)}</span>
          {r.recordedAt && (
            <Tooltip label={`Recorded ${formatIst(r.recordedAt)}${r.actorName ? ` by ${r.actorName}` : ''}`}>
              <span className="block whitespace-nowrap type-caption text-subtle">
                {formatIst(r.recordedAt, 'hh:mm A')}
              </span>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      key: 'kind', title: 'Type', dataIndex: 'kind', sortable: true, width: 175,
      render: (_: any, r: any) => (r.kind === 'CASH_MOVEMENT'
        // A contra entry is neither a collection nor a payout — it gets its own tone, and the remark
        // (the only explanation it carries) rides along as the tooltip.
        ? <Tooltip label={r.remark || ''}><Tag tone={ENTRY_TONE.MOVEMENT} style={{ margin: 0 }}>{movementLabel(r, isCashBucket)}{r.reversed ? ' · reversed' : ''}</Tag></Tooltip>
        : r.kind === 'PAYOUT'
          ? <Tag tone={ENTRY_TONE.PAYOUT} style={{ margin: 0 }}>{PAYOUT_LABEL[r.payoutType] ?? r.payoutType}</Tag>
          : <Tag tone={ENTRY_TONE.COLLECTION} style={{ margin: 0 }}>Collection</Tag>),
    },
    // Identifiers TRUNCATE with the full value on hover — an untruncated long document or order number
    // overflowed its fixed column and printed over the neighbouring cell.
    {
      key: 'docNo', title: 'Document', dataIndex: 'docNo', sortable: true, width: 155,
      /* A COLLECTION's number opens the payment itself — the payments list takes `?focus=` and pops
         that record's review panel. Wallet transactions and cash movements have no detail page to
         open, so they stay as plain text rather than pretending to be links. */
      render: (v: any, r: any) => (
        <span className="block min-w-0 leading-tight">
          {r.kind === 'COLLECTION' && v
            ? <Link to={`/billing/payments?tab=list&focus=${encodeURIComponent(v)}`}
                className="block truncate text-brand-700 hover:underline dark:text-brand-400" title={v}
                onClick={(e: any) => e.stopPropagation()}>{v}</Link>
            : <span className="block truncate text-content" title={v ?? ''}>{v}</span>}
          {/*
            * THE RECONCILING ITEM, named on the row it belongs to.
            *
            * This deposit is somebody's claim that money reached the account; nobody has checked it
            * yet, so it is deliberately not in the running net or the totals. Saying so here is what
            * keeps the register honest: the reader can see the line, see that the balance did not
            * move, and know why — rather than finding a row whose arithmetic appears to be broken.
            */}
          {r.pendingVerification && (
            <Tag tone="gold" style={{ margin: 0 }} className="mt-0.5">Awaiting confirmation</Tag>
          )}
        </span>
      ),
    },
    {
      key: 'party', title: 'Party', width: 195, sortable: true,
      sorter: (a: any, b: any) => String(a.partyName ?? '').localeCompare(String(b.partyName ?? '')),
      // A collection's party is a retailer; a payout's is the staff member who was paid. Both link —
      // UserLink self-gates on USER.VIEW and never links a Super Admin.
      render: (_: any, r: any) => (r.kind === 'CASH_MOVEMENT'
        /* The counterparty is the OTHER side of the contra — the bank account when you are reading the
           cash book, cash in hand when you are reading a bank. The purpose and who handled it sit
           underneath, because "which bank" is the question this column is asked. */
        ? (
          <span className="block min-w-0" title={[r.counterpartyNote, r.remark].filter(Boolean).join(' — ')}>
            <span className="block truncate text-content">{r.partyName ?? '—'}</span>
            {r.counterpartyNote && <span className="block truncate type-caption text-subtle">{r.counterpartyNote}</span>}
          </span>
        )
        : r.kind === 'COLLECTION'
          ? <RetailerLink id={r.retailerId}>{r.partyName ?? `#${r.retailerId}`}</RetailerLink>
          : (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate"><UserLink id={r.partyUserId} role={r.partyRole}>{r.partyName ?? '—'}</UserLink></span>
              {/* Who this person IS, badged the way every other list in the app badges a name. */}
              {r.partyRole && <RoleBadge role={r.partyRole} />}
            </span>
          )),
    },
    { key: 'order', title: 'Order', dataIndex: 'orderNo', sortable: true, width: 155, render: (_: any, r: any) => (r.orderNo
      ? <span className="block truncate" title={r.orderNo}><OrderLink id={r.orderId}>{r.orderNo}</OrderLink></span>
      : <span className="text-subtle">—</span>) },
    // Wide enough for BANK_TRANSFER — the payment modes here come from the wallet too, and the
    // collections-sized column (NEFT/RTGS/UPI) let the longer ones spill into Reference.
    { key: 'mode', title: 'Mode', dataIndex: 'mode', sortable: true, width: 125, render: (v: any) => <span className="block truncate text-muted" title={v ?? ''}>{v ?? '—'}</span> },
    /*
     * WHO IS HOLDING IT. Only cash has a custodian; every other mode went straight to a bank, so the
     * cell stays empty rather than inventing a state for money that never passed through a person.
     *
     * This column exists because the bank register showed a cash collection with an empty bank cell
     * and an "Assign" button, which read as money nobody had accounted for. A rep's collection is
     * accounted for — it is in their hands, and this says so.
     */
    {
      key: 'custody', title: 'Cash with', width: 210,
      render: (_: any, r: any) => {
        /*
         * A CASH MOVEMENT HAS A CUSTODIAN TOO.
         *
         * This column answers "who had the notes", and it printed a dash on every bank↔cash movement
         * — including a rep's deposit, which is the one row where the answer is most certain: somebody
         * was carrying that money right up until they banked it, and their name is on the record.
         */
        if (r.kind === 'CASH_MOVEMENT') {
          if (!r.handledByName) return <span className="text-subtle">—</span>;
          return (
            <span className="block min-w-0 leading-tight">
              <span className="truncate">
                {r.handledById
                  ? <UserLink id={r.handledById}>{r.handledByName}</UserLink>
                  : <span className="text-content">{r.handledByName}</span>}
              </span>
              <span className="block truncate type-caption text-subtle">
                {r.movementKind === 'DEPOSIT' ? 'was holding it' : 'took the cash'}
              </span>
            </span>
          );
        }
        if (r.kind !== 'COLLECTION' || String(r.mode).toUpperCase() !== 'CASH') return <span className="text-subtle">—</span>;
        if (!r.collectedById || r.custodyState === 'UNASSIGNED') {
          return <Tag tone="red" style={{ margin: 0 }}>Custodian unknown</Tag>;
        }
        /*
         * WHO TOOK IT. Not whether it has been banked.
         *
         * Cash is fungible, and that is the whole argument. Once ₹80,000 is in a rep's bag there is no
         * fact of the matter about WHICH ₹30,000 of it later reached an account — FIFO picks the oldest
         * collection so the money has a provenance to follow, but that is an accounting convention, not
         * a claim about particular notes. Stamping "not yet banked" on an individual collection asserted
         * a precision that does not exist, and on a partial deposit it read as plainly wrong: a rep who
         * banked ₹60,000 of ₹80,000 still had "Not yet banked" against rows adding up to far more.
         *
         * Banked-ness is a property of the PERSON, not of a payment. It is one number — their collection
         * money outstanding — and it belongs on them, where it is shown: their own two-book ledger, the
         * staff money card, and the holders list. Here the honest column is the custodian's name.
         */
        return (
          <span className="block min-w-0 leading-tight">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate"><UserLink id={r.collectedById} role={r.collectedByRole}>{r.collectedByName}</UserLink></span>
              <RoleBadge role={r.collectedByRole} />
            </span>
          </span>
        );
      },
    },
    { key: 'ref', title: 'Reference', width: 150, render: (_: any, r: any) => <span className="block truncate text-muted" title={r.reference ?? ''}>{r.reference || <span className="text-subtle">—</span>}</span> },
    {
      key: 'in', title: 'Money in', dataIndex: 'signed', sortable: true, width: 125,
      render: (_: any, r: any) => (r.signed > 0
        ? <PendingAware r={r} text={inrT(r.amount)} tone="text-success-600 dark:text-success-500" />
        : <span className="text-subtle">—</span>),
    },
    {
      key: 'out', title: 'Money out', width: 125,
      sortable: true, sorter: (a: any, b: any) => (a.signed < 0 ? -a.signed : 0) - (b.signed < 0 ? -b.signed : 0),
      render: (_: any, r: any) => (r.signed < 0
        ? <PendingAware r={r} text={`−${inrT(r.amount)}`} tone="text-danger-600 dark:text-danger-500" />
        : <span className="text-subtle">—</span>),
    },
    { key: 'running', title: 'Running net', dataIndex: 'running', sortable: true, width: 135, render: (v: any) => <span className="whitespace-nowrap text-muted">{inrT(v)}</span> },
    // Reassigning the received-in bank only makes sense for a collection; a payout's account comes from
    // the wallet ledger row and is corrected there, not here.
    ...(canEdit ? [{
      key: 'assign', title: '', width: 120, align: 'right' as const,
      render: (_: any, r: any) => (r.kind === 'CASH_MOVEMENT'
        ? <ReverseMovementControl row={r} canReverse={canReverseMovement} />
        /* Available on EVERY collection, cash included.
           It was hidden for cash while a hand-over workflow existed: a rep's collection was waiting for
           them to bring the notes in, not for an account to be named, so offering "assign a bank" there
           invited booking money into an account it had not reached. That workflow is gone. A cash
           payment now waits for exactly one thing — a company bank receipt — so naming the account it
           landed in is the whole point rather than a trap. */
        : r.kind === 'COLLECTION'
        /* The ROW's account, not the bucket. On the all-accounts register the bucket is the literal
           'ALL', so every collection opened its picker saying "currently —" — on payments that have
           had an account named against them all along. */
        ? <AssignBankControl payment={{ id: r.id, retailerId: r.retailerId, receivedBankAccountId: r.receivedBankAccountId ?? null, paymentNo: r.docNo, mode: r.mode }} banks={bankAccountsRaw} />
        : null),
    }] : []),
  ];

  // Six filters is past what a flat toolbar can carry — as a bare row they wrapped, took six different
  // widths and gave every filter equal weight. They move into the shared in-place panel instead:
  // labelled, uniform, grouped by the COLUMN they narrow and in column order (Type · Document · Party,
  // then Mode · Reference, then the Money in/out pair), with what is applied always visible as chips.
  // `.title`, not `.label`: an option's label is a node (dot + text) and a node in a template
  // literal prints "[object Object]". `hueOption` keeps the plain words on `title` for exactly this.
  const typeLabel = typeOptions.find((o) => o.value === fType)?.title ?? fType;
  const chips: FilterChip[] = [
    ...(fType ? [{ key: 'type', label: `Type: ${typeLabel}`, onRemove: () => setFType(undefined) }] : []),
    ...(fDoc.trim() ? [{ key: 'doc', label: `Document: ${fDoc.trim()}`, onRemove: () => setFDoc('') }] : []),
    ...(fParty.trim() ? [{ key: 'party', label: `Party: ${fParty.trim()}`, onRemove: () => setFParty('') }] : []),
    ...(fMode ? [{ key: 'mode', label: `Mode: ${fMode}`, onRemove: () => setFMode(undefined) }] : []),
    ...(fFrom ? [{ key: 'from', label: `From ${fFrom.format('DD MMM YYYY')}`, onRemove: () => setFFrom(null) }] : []),
    ...(fTo ? [{ key: 'to', label: `Up to ${fTo.format('DD MMM YYYY')}`, onRemove: () => setFTo(null) }] : []),

    ...(fRef.trim() ? [{ key: 'ref', label: `Reference: ${fRef.trim()}`, onRemove: () => setFRef('') }] : []),
    ...(fDir ? [{ key: 'dir', label: fDir === 'IN' ? 'Money in' : 'Money out', onRemove: () => setFDir(undefined) }] : []),
    ...(fNeedsBank ? [{ key: 'needsbank', label: 'Awaiting a bank account', onRemove: () => setFNeedsBank(false) }] : []),
    ...(fCashBook ? [{ key: 'cashbook', label: 'Office cash book', onRemove: () => setFCashBook(false) }] : []),
  ];
  const filters = useFilterPanel({
    onClear: clearFilters,
    chips,
    groups: [
      {
        /*
         * EACH SECTION TAKES AN IDENTITY HUE AND A GLYPH.
         *
         * Four grey headings in a row means reading all four, every time, to find the one you want.
         * Hued, the heading, its glyph and the rule under it say WHICH section at a glance — and only
         * those three: a background wash would turn the panel into four coloured cards and the tints
         * would be what the eye lands on before the filters. The hues come from `IDENTITY_SERIES`
         * (no red, no brown): a section colour says which section, never that something is wrong.
         */
        title: 'Entry',
        tone: PALETTE.brand[500],
        icon: <Receipt size={13} />,
        fields: [
          { label: 'Type', control: <Select placeholder="Any type" allowClear value={fType} onChange={(v) => setFType(v as string | undefined)} options={typeOptions} /> },
          { label: 'Document', control: <Input allowClear placeholder="e.g. PAY/2026/000056" value={fDoc} onChange={(e: any) => setFDoc(e.target.value)} /> },
          { label: 'Party', control: <Input allowClear placeholder="e.g. Demo Retailer" value={fParty} onChange={(e: any) => setFParty(e.target.value)} /> },
          {
            // The bank desk's worklist, next to the rows it narrows — and what the "To assign" tile
            // switches on when it opens this register.
            label: 'Attribution',
            control: (
              <Select placeholder="Any" allowClear value={fNeedsBank ? '1' : undefined}
                onChange={(v) => setFNeedsBank(v === '1')}
                /* The gold of the "To assign" tile this filter is the register for. */
                options={[hueOption('1', 'Awaiting a bank account', TAG_TONE_HUE.gold)]} />
            ),
            hint: 'non-cash collections that still name no account',
          },
          {
            /* The slice the "Cash in hand" balance is made of: cash the office holds — not a rep's
               collections (those are their custody) and not an unattributed UPI payment (that is in
               a bank, we just do not know which). */
            label: 'Cash book',
            control: (
              <Select placeholder="Any" allowClear value={fCashBook ? '1' : undefined}
                onChange={(v) => setFCashBook(v === '1')}
                /* Gold again, and deliberately: this is the slice the gold "Cash in hand" tile counts. */
                options={[hueOption('1', 'Office cash book only', TAG_TONE_HUE.gold)]} />
            ),
            hint: 'what makes up cash in hand',
          },
        ],
      },
      {
        title: 'Payment',
        tone: PALETTE.sky[500],
        icon: <CreditCard size={13} />,
        fields: [
          { label: 'Mode', control: <Select placeholder="Any mode" allowClear value={fMode} onChange={(v) => setFMode(v as string | undefined)} options={modeOptions} /> },
          { label: 'Reference', control: <Input allowClear placeholder="e.g. UTR-E2E-001" value={fRef} onChange={(e: any) => setFRef(e.target.value)} />, hint: 'UTR, cheque or receipt reference' },
        ],
      },
      {
        // The period sits with the register now: it is the window these rows are read over, and its
        // effect is visible the moment it changes.
        title: 'Period',
        tone: PALETTE.harvest[400],
        icon: <CalendarDays size={13} />,
        fields: [
          {
            label: 'Between',
            // TWO cells wide: it holds two controls, and the panel sizes a group from the cells its
            // fields declare. At one cell the pair was squeezed to "Start dat…"/"End date".
            span: 2 as const,
            // Fused into one shell so the pair reads as ONE control, and each half flexes to the cell
            // instead of insisting on its own 9rem and spilling into the next column.
            control: (
              <FilterRange
                from={<DatePicker placeholder="Start date" disabledDate={disableFuture} value={fFrom} onChange={(d: any) => setFFrom(d ?? null)} />}
                to={<DatePicker placeholder="End date" disabledDate={disableFuture} value={fTo} onChange={(d: any) => setFTo(d ?? null)} />}
              />
            ),
            hint: 'Either end can stand alone — leave both empty for everything on this account',
          },
        ],
      },
      {
        // Violet, not green or red: the heading names a section that holds BOTH directions, and the
        // two options inside it are where green-in and red-out belong.
        title: 'Money in / out',
        tone: PALETTE.plum[400],
        icon: <ArrowLeftRight size={13} />,
        fields: [
          {
            label: 'Direction',
            control: (
              <Select placeholder="Both" allowClear value={fDir} onChange={(v) => setFDir(v as string | undefined)}
                options={[
                  /* The same green-in / red-out pair the KPI tiles and the entry counts use, so the
                     direction is recognised before the words are read. */
                  /* These two keep ARROWS where the other filters take a dot: the arrow sits in the
                     same place, says more than a dot can here, and is already what the tiles and the
                     Money in / Money out columns use. `title` is the plain text — it is what the
                     option is searched by and what a clipped trigger shows on hover. */
                  {
                    value: 'IN',
                    title: 'Money in',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <ArrowDownLeft size={14} className="text-success-600 dark:text-success-500" />Money in
                      </span>
                    ) as any,
                  },
                  {
                    value: 'OUT',
                    title: 'Money out',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <ArrowUpRight size={14} className="text-danger-600 dark:text-danger-500" />Money out
                      </span>
                    ) as any,
                  },
                ]} />
            ),
          },
        ],
      },
    ],
  });

  return (
    <>
      {/* A reconciliation view must never quietly drop rows — say so when the server capped the set. */}
      {data?.truncated && (
        <div className="mb-2 rounded-control bg-warning-500/10 px-3 py-2 text-xs text-warning-600 dark:text-harvest-300">
          Showing the first {rows.length.toLocaleString('en-IN')} entries only — narrow the date range to see the rest.
          The Export CSV file is not capped this way.
        </div>
      )}
      {/*
        * WHAT EXPORT CAN AND CANNOT CARRY.
        *
        * The file is produced by the server, which understands the period, the mode and the retailer.
        * Type, document, party text, reference and direction are applied to the fetched rows in the
        * browser and have no server equivalent — so a file taken while they are set would quietly
        * contain rows the screen is hiding. Say so where the reader is about to click Export, rather
        * than letting them discover it in a spreadsheet.
        */}
      {clientOnlyActive.length > 0 && (
        <div className="mb-2 rounded-control border border-warning-500/40 bg-warning-500/10 px-3 py-2 type-caption text-warning-700 dark:text-harvest-300">
          Export carries the period and mode, but not {clientOnlyActive.join(', ')} — the file will hold
          every entry those are currently hiding.
        </div>
      )}
      {/* NAME WHAT IS NOT HERE. A deposit claim a verifier turned down moved no money, so it is in no
          figure on this page — but somebody who remembers recording it would otherwise find it simply
          gone, with nothing to say why. */}
      {Number(data?.rejectedMovements?.count ?? 0) > 0 && (
        <div className="mb-2 type-caption text-subtle">
          {data.rejectedMovements.count} deposit claim{data.rejectedMovements.count === 1 ? '' : 's'} worth{' '}
          {inrT(data.rejectedMovements.total)} {data.rejectedMovements.count === 1 ? 'was' : 'were'} turned down
          on checking — no money moved, so they are not listed or counted here.
        </div>
      )}
      <DataTable
        storageKey="bank-register"
        rowKey="_key"
        layoutFixed
        loading={isLoading}
        dataSource={rows}
        columns={stmtCols}
        rowClassName={focus.rowClassName}
        /*
         * PAGED FOR READING, whole for the arithmetic.
         *
         * An active account runs to hundreds of lines, and the register was rendering every one of
         * them — a wall to scroll past to reach anything, and a slow first paint on the busiest
         * account in the business.
         *
         * Safe to page because the running net is computed BEFORE this: the whole filtered set is put
         * in date order, the balance accumulated down it, and each row keeps the balance as of its own
         * date. Splitting the display therefore changes nothing about the figures — page two's first
         * row carries exactly the balance it had when the whole list was on screen. The header totals
         * come from the same full set, so they never describe only the visible page.
         */
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], hideOnSinglePage: true,
          showTotal: (total: number, [from, to]: [number, number]) => `${from}–${to} of ${total} entries` }}
        emptyText={filterCount ? 'No entries match these filters' : 'Nothing moved through this account for these filters'}
        toolbarLeft={<>{filters.trigger}{filters.chips}</>}
        toolbarBelow={filters.panel}
        toolbarRight={
          <div className="flex items-center gap-2">
            {/* Totals for the filtered set, so the toolbar can never disagree with the rows under it. */}
            {filterCount > 0 && (
              <span className="whitespace-nowrap text-xs text-muted">
                {rows.length} of {all.length} · in <span className="font-medium text-success-600 dark:text-success-500">{inrT(shown.received)}</span> · out <span className="font-medium text-danger-600 dark:text-danger-500">{inrT(shown.paidOut)}</span> · net <b>{inrT(shown.net)}</b>
                {shown.awaiting > 0.005 && (
                  <> · <span className="text-warning-600 dark:text-harvest-300">{inrT(shown.awaiting)} claimed, not yet confirmed</span></>
                )}
              </span>
            )}
            {/*
              * EVERY filter the reader can see, not three of them.
              *
              * Only from / to / mode used to reach the export, so narrowing the register to one party
              * or to money-out and pressing Export produced a file with all the other rows still in
              * it — under a heading that says it is this register. The server applies the same five
              * predicates to the same finished statement, so the file is the screen.
              */}
            {actions?.({
              ...(fFrom ? { from: fFrom.format('YYYY-MM-DD') } : {}),
              ...(fTo ? { to: fTo.format('YYYY-MM-DD') } : {}),
              ...(fMode ? { mode: fMode } : {}),
              ...(fType ? { type: fType } : {}),
              ...(fDir ? { direction: fDir } : {}),
              ...(fDoc.trim() ? { docNo: fDoc.trim() } : {}),
              ...(fRef.trim() ? { reference: fRef.trim() } : {}),
              ...(fParty.trim() ? { party: fParty.trim() } : {}),
              ...(fNeedsBank ? { needsBank: '1' } : {}),
              ...(fCashBook ? { cashBook: '1' } : {}),
            })}
          </div>
        }
      />
    </>
  );
}

/**
 * Undo a cash movement recorded in error. The original row STAYS and a mirror entry cancels it, so the
 * account still shows that the money moved and that it came back — deleting it would leave a matched
 * bank statement line pointing at nothing. Gated on CASH.MOVEMENT.REVERSE (Admin / Super Admin).
 */
function ReverseMovementControl({ row, canReverse }: { row: any; canReverse: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const save = useConfirmedMutation({ title: "Reverse this cash movement?", content: "A reversing entry is posted. The original movement stays on record with your reason against it.", okText: "Yes, reverse", danger: true }, {
    mutationFn: () => request({ url: `/cash-movements/${row.id}/reverse`, method: 'POST', data: { reason } }),
    onSuccess: (r: any) => {
      const d = r?.data ?? r;
      message.success(`${d?.txnNo ?? row.docNo} reversed by ${d?.reversalNo ?? 'a mirror entry'}`);
      setOpen(false); setReason('');
      qc.invalidateQueries({ queryKey: ['payments-bank-summary'] });
      qc.invalidateQueries({ queryKey: ['bank-register'] });
      qc.invalidateQueries({ queryKey: ['cash-in-hand'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to reverse'),
  });
  // A reversed entry, and the mirror that reversed it, are both final.
  if (!canReverse || row.reversed || row.isReversal) return null;
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setReason(''); }}
      placement="bottomRight"
      overlayClassName="max-w-[22rem]"
      content={
        <div className="w-80 p-1" onClick={(e) => e.stopPropagation()}>
          <div className="mb-2 flex items-center gap-2 type-body-strong"><Undo2 size={16} /> Reverse this movement</div>
          <div className="mb-2 text-xs text-muted">
            {row.docNo} · {inrT(row.amount)} · {row.purposeLabel ?? ''}
          </div>
          <div className="mb-1 type-overline text-muted">Reason</div>
          <Input.TextArea rows={2} maxLength={500} value={reason} onChange={(e: any) => setReason(e.target.value)}
            placeholder="e.g. recorded against the wrong account" />
          <div className="mt-1 type-caption text-subtle">
            The original entry stays. A mirror entry, dated today, puts the money back.
          </div>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="text" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" variant="primary" loading={save.isPending} disabled={reason.trim().length < 3} onClick={() => save.mutate()}>
              Reverse
            </Button>
          </div>
        </div>
      }
    >
      <Button size="sm" variant="text" icon={<Undo2 size={14} />} onClick={(e) => e.stopPropagation()}>Reverse</Button>
    </Popover>
  );
}

/** Picker sentinel for "no bank" — bank account ids are numeric, so this can never collide with one. */
const CASH_OPTION = '__CASH__';

/**
 * Per-row "Assign / Reassign to bank" control — sets a payment's received-in bank account (moving it
 * out of the Cash / Unassigned bucket, or correcting a wrong account). Legacy UPI payments recorded
 * before the received-in-bank was mandatory, and cash deposited into a bank, both land here. Reuses
 * PATCH /payments/:id (bank-only change → no ledger/amount side-effects; gated PAYMENT.EDIT).
 *
 * The picker also offers Cash / Unassigned, so a payment assigned to the wrong account can be put BACK
 * (cash kept in hand rather than banked). The server only allows that for a CASH-mode payment —
 * everything else must record the account it landed in — so for a non-cash payment the choice is shown
 * DISABLED with the reason, instead of being offered and then refused on save.
 */
function AssignBankControl({ payment, banks }: { payment: any; banks: any[] }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const current: string | undefined = payment.receivedBankAccountId ?? undefined;
  const primaryId = banks.find((b) => b.isPrimary) ? String(banks.find((b) => b.isPrimary).id) : undefined;
  // Default the picker to the current account, else the primary one — so an unassigned payment is a
  // one-click assign-to-primary.
  const [bankId, setBankId] = useState<string | undefined>(current ?? primaryId);
  // Where the payment sits TODAY in picker terms — no bank account = the cash bucket.
  const currentSel = current ?? CASH_OPTION;
  const isCashMode = String(payment.mode ?? '').toUpperCase() === 'CASH';
  const toCash = bankId === CASH_OPTION;
  const options = [
    {
      value: CASH_OPTION,
      disabled: !isCashMode,
      searchLabel: 'Cash / Unassigned no bank cash in hand',
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <Wallet size={14} className="shrink-0 text-muted" />
          <span className="truncate">Cash / Unassigned</span>
          <span className="shrink-0 type-caption text-subtle">{isCashMode ? 'no bank' : 'cash mode only'}</span>
        </span>
      ),
    },
    ...banks.map(bankAcctOption),
  ];
  const save = useConfirmedMutation({ title: "Move this payment to a different account?", content: "The bank collection figures for both accounts change to match.", okText: "Yes, reassign" }, {
    mutationFn: () => request({ url: `/payments/${payment.id}`, method: 'PATCH', data: { receivedBankAccountId: toCash ? '' : (bankId ?? '') } }),
    onSuccess: () => {
      message.success(toCash ? 'Moved to Cash / Unassigned' : 'Bank assigned');
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['payments-bank-summary'] });
      // The register under the summary is keyed 'bank-register' — invalidating 'bank-statement' (no such
      // key) left the moved row sitting in the bucket it had just left until a manual refresh.
      qc.invalidateQueries({ queryKey: ['bank-register'] });
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to assign bank'),
  });
  const isReassign = !!current;
  const currentLabel = current ? (banks.find((b) => String(b.id) === String(current)) ? bankAcctOption(banks.find((b) => String(b.id) === String(current))).searchLabel : '—') : 'Cash / Unassigned';
  return (
    <Popover
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) setBankId(current ?? primaryId); }}
      placement="bottomRight"
      // The body is a 20rem picker card — lift the Popover's default text-popover width cap so the
      // Select and the Cancel/Assign row stay INSIDE the card instead of spilling past its edge.
      overlayClassName="max-w-[22rem]"
      content={
        <div className="w-80 p-1" onClick={(e) => e.stopPropagation()}>
          <div className="mb-2 flex items-center gap-2 type-body-strong">
            <Landmark size={16} /> {isReassign ? 'Reassign' : 'Assign'} to bank account
          </div>
          <div className="mb-2 text-xs text-muted">
            {payment.paymentNo} · {payment.mode} · currently <span className="font-medium text-content">{currentLabel}</span>
          </div>
          <div className="mb-1 type-overline text-muted">Received in</div>
          <Select placeholder="Select bank account" showSearch optionFilterProp="searchLabel" style={{ width: '100%' }}
            value={bankId} onChange={(v) => setBankId(v as string)} options={options} />
          {!isCashMode && (
            <div className="mt-1.5 type-caption text-subtle">A {payment.mode || 'non-cash'} payment must record the bank account it was received in.</div>
          )}
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="text" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" variant="primary" loading={save.isPending} disabled={!bankId || bankId === currentSel} onClick={() => save.mutate()}>
              {toCash ? 'Move to cash' : isReassign ? 'Reassign' : 'Assign'}
            </Button>
          </div>
        </div>
      }
    >
      <Button size="sm" variant={isReassign ? 'text' : 'ghost'} icon={<Landmark size={14} />} onClick={(e) => e.stopPropagation()}>
        {isReassign ? 'Reassign' : 'Assign'}
      </Button>
    </Popover>
  );
}
